/* eslint-disable valid-jsdoc */
/* eslint-disable camelcase */
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { TRADING_ELOQUENT as ELOQUENT } from 'config/constants';
import { queryClient, queryClientKeys } from 'config/executors/apiInterceptor';
import { useMemo } from 'react';

/**
 * Get order by id
 * @param {Any} id
 * @returns {Promise}
 */
export function getOrderById(id) {
    return ELOQUENT.Orders.show(id);
}

/**
 * Update order
 * @param {Any} id
 * @param {Object} data
 * @returns {Promise}
 */
export function updateOrder(id, data) {
    return ELOQUENT.Orders.update(id, data);
}

/**
 * Cancel order
 * @param {Any} id
 * @returns {Promise}
 */
export function cancelOrder(id) {
    return ELOQUENT.Orders.cancel(id);
}

export const useCancelOrderApi = (apiCaller) => {
    return useMutation({
        mutationFn: async (id) => await apiCaller(cancelOrder, id)
    });
};

/**
 * Get a product orders
 * @param {Any} product_id
 * @param {Any} type
 * @returns {Promise}
 */
export function getProductOrders(product_id, type = null, { token }) {
    return ELOQUENT.Orders.index({ product_id, type, per_page: 100 }, token);
}

/**
 * Get a product orders
 * @param {Object} data
 * @returns {Promise}
 */
export function createOrder(data) {
    return ELOQUENT.Orders.create(data);
}

/**
 * Get order logs
 * @param {Any} id
 * @returns {Promise}
 */
export function getLogs(id) {
    return ELOQUENT.Orders.logs(id);
}

/**
 * Cancel active orders
 * @param {Any} data
 * @returns {Promise}
 */
export function cancelOrders(data, { token }) {
    return ELOQUENT.BulkOrders.cancel(data, token);
}

/**
 * submit reorders
 * @param {Object} data
 * @returns {Promise}
 */
export function submitOrderBookReorders(data) {
    return ELOQUENT.Orders.regenerate(data);
}

/**
 * Secondary Clients
 * @param {Number} orderId
 * @returns {Promise}
 */
export function secondaryClients(orderId) {
    return ELOQUENT.Orders.secondaryClients(orderId);
}

/**
 * Secondary Clients Query
 * @param {Function} apiCaller
 * @param {Number} orderId
 * @returns {import('@tanstack/react-query').UseQueryResult<any, unknown>}
 */
export const useSecondaryClientssApi = (apiCaller, orderId) => {
    const query = useQuery({
        queryKey: [queryClientKeys.useSecondaryClientssApi, { orderId }],
        queryFn: async (ee) => {
            const orderId = ee.queryKey[1].orderId;

            return await apiCaller(secondaryClients, orderId);
        }
    });

    return { ...query, data: query.data?.data ?? [] };
};

/**
 * Secondary Clients
 * @param {Number} orderId
 * @param {Object} data
 * @returns {Promise}
 */
export function bookFuture(orderId, data, { token }) {
    return ELOQUENT.Orders.bookFuture(orderId, data, token);
}

/**
 * Get frieght options for destination
 * @param {Any} data {per_page, page, search}
 * @returns {Promise}
 */
export function getFrieghtDestinations(data) {
    return ELOQUENT.Destinations.index(data);
}

export const refreshFrieghtDestinations = () => {
    queryClient.invalidateQueries({
        queryKey: [queryClientKeys.useFrieghtDestinationsInfinite]
    });
};

export function useFrieghtDestinationsInfinite(apiCaller, initialPageParam, enabled, format) {
    const api = useInfiniteQuery({
        queryKey: [queryClientKeys.useFrieghtDestinationsInfinite, initialPageParam],
        initialPageParam: initialPageParam,
        enabled,
        refetchOnMount: true,
        queryFn: (ee) => {
            const dataF = ee.queryKey[1];

            return apiCaller(getFrieghtDestinations, { ...dataF, ...ee.pageParam });
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (!lastPage?.data?.next_page_url) return undefined;

            return {
                ...lastPageParam,
                page: lastPage?.data?.next_page_url
                    ? lastPage?.data?.current_page + 1
                    : lastPage?.data?.current_page
            };
        }
    });

    const finalData = useMemo(() => {
        const mergedPages = api.data?.pages.reduce((prev, cur) => {
            return [...prev, ...cur.data.data];
        }, []) ?? [];

        return format ? format(mergedPages) : mergedPages;
    }, [api.data, format]);

    return { ...api, data: finalData };
}

/**
 * Get frieght options for origin
 * @param {Any} data {per_page, page, search}
 * @returns {Promise}
 */
export function getFrieghtOrigins(data) {
    return ELOQUENT.Origins.index(data);
}

export const refreshFrieghtOrigins = () => {
    queryClient.invalidateQueries({
        queryKey: [queryClientKeys.useFrieghtOriginsInfinite]
    });
};

export function useFrieghtOriginsInfinite(apiCaller, initialPageParam, enabled, format) {
    const api = useInfiniteQuery({
        queryKey: [queryClientKeys.useFrieghtOriginsInfinite, initialPageParam],
        initialPageParam: initialPageParam,
        enabled,
        refetchOnMount: true,
        queryFn: (ee) => {
            const dataF = ee.queryKey[1];

            return apiCaller(getFrieghtOrigins, { ...dataF, ...ee.pageParam });
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (!lastPage?.data?.next_page_url) return undefined;

            return {
                ...lastPageParam,
                page: lastPage?.data?.next_page_url
                    ? lastPage?.data?.current_page + 1
                    : lastPage?.data?.current_page
            };
        }
    });

    const finalData = useMemo(() => {
        const mergedPages = api.data?.pages.reduce((prev, cur) => {
            return [...prev, ...cur.data.data];
        }, []) ?? [];

        return format ? format(mergedPages) : mergedPages;
    }, [api.data, format]);

    return { ...api, data: finalData };
}
